<template>
    <div>
        <div class="revenue__page  min-h-inherit">
            <div class="revenue__goals__wrap">
                <div class="revenue__goals__header">
                    <div>
                        <p class="text--semi--bold text--sm text-gray-700">
                            Set revenue goals and targets
                        </p>
                        <p class="text--normal text--sm text-gray-600 mt--5">
                            Maximize and measure your revenue potential as a creator by setting attainable goals.
                        </p>
                    </div>
                </div>

                <div class="revenue__goals__body">
                    <EmptyPage 
                        state="empty_custom"
                        btn-state="none" 
                        icon-name="hard-hat" 
                        type="goal"
                        position="left"
                        title="Coming soon!"
                        subtitle="We're working hard to bring this feature to you. Stay tuned for updates."
                    />
                </div>
            </div>
        </div>

        <MainModal 
            :show-modal="showGoalModal"  
            size="sm" 
            :title="`${formActionType === 'add' ? 'New' : 'Edit'} revenue goal`" 
            @close="resetCloseGoalModal"
        >
            <template #body>
                <form>
                    <div>
                        <FormInput 
                            id="amount"
                            ref="revenueGoalInput" 
                            v-model="form.name" 
                            info-type="required"
                            no-margin-bottom 
                            size="sm" 
                            label="Name"
                            :autofocus="autoFocusGoalInput"
                            :is-error="!!formGoalsErrors.name"
                            :err-msg="formGoalsErrors.name"
                            placeholder="Make 100m in sales by the end of March"
                        />

                        <div class="form__row mb--15 mt--15 pl--3">
                            <div class="flex-1 mr--5">
                                <FormLabel
                                    info-type="required" 
                                    html-for="revenueGoalInput" 
                                    title="Target type"
                                />
                                <CustomDropdown 
                                    id="targetDropdown" 
                                    v-model="form.targetType" 
                                    :options="targets"
                                    size="md"
                                    list-type="countries" 
                                    index-label="name"
                                    :searchable="false"
                                    @select="v => form.targetType = v" 
                                />
                            </div>

                            <div class="flex-1 mr--5">
                                <FormLabel title="Target amount" html-for="hourlyRate" info-type="required" />
                                <money
                                    v-model="form.amount" 
                                    v-bind="config.number"
                                    :prefix="currencySymbol"
                                    class="form-control form-control-sm"
                                    spellcheck="false" 
                                    @keydown.native="preventKeys"
                                />
                            </div>
                        </div>

                        <div class="mt--15">
                            <FormLabel title="Due date" info-type="required" />
                            <v-date-picker v-model="form.dueDate" class="text--sm">
                                <template #default="{ inputValue, togglePopover }">
                                    <input
                                        class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                        :value="inputValue"
                                        @click="togglePopover"
                                    >
                                </template>
                            </v-date-picker>
                        </div>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-align-items-center">
                    <BaseButton
                        class="mr--5" 
                        label="Cancel"
                        variant="outline" 
                        :disabled="formGoalState === 'loading'"
                        @submit="resetCloseGoalModal"
                    />
                    <BaseButton 
                        :label="formActionType === 'add' ? 'Add goal' : 'Save updates'"
                        :loading="formGoalState === 'loading'" 
                        :disabled="btnDisabledCEditModal"
                        type="submit" 
                        @submit="handleGoalSubmit"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import { 
    EmptyPage, BaseButton, 
    MainModal, FormInput, FormLabel, CustomDropdown,
} from "@/components/ui/index";
import { supportedCurrenciesMap } from "@/plugins/countries";

export default {
    name: "RevenueGoals",

    components: {
        EmptyPage,
        BaseButton,
        MainModal,
        FormInput,
        CustomDropdown,
        FormLabel,
    },

    data() {
        return {
            supportedCurrenciesMap,
            form: {
                name: "",
                isCompleted: false,
                targetType: "",
                dueDate: null,
                amount: 0,
            },
            formActionType: "add",
            formGoalState: "default",
            showGoalModal: false,
            deleteLoading: false,
            pageLoading: false,
            pageLoadingErr: false,
            showScrollLoader: false,
            autoFocusGoalInput: false,
            targets: ["Monthly", "Yearly", "Quartely", "One-Off"],
            formGoalsErrors: {},
            config: {
                number: {
                    decimal: ".",
                    thousands: ",",          // The currency name or symbol followed by a space.
                    prefix: "",
                    suffix: "",
                    precision: 0,
                    masked: false,
                },
            },
        };
    },

    computed: {
        goals() {
            return this.$store.getters["revenue/goals"]?.list;
        },

        pageData() {
            return this.$store.getters["revenue/goals"].pageData;
        },

        showPagination() {            
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        completedGoals() {
            return this.goals.filter(x => x.isCompleted).length || 0;
        },

        // currency() {
        //     return this.$store.getters["revenue/currency"];
        // },

        currency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        currentYear() {
            return this.$store.getters["revenue/currentYear"];
        },

        btnDisabledCEditModal(){
            return !this.form.name || !this.form.dueDate || !this.form.targetType || !this.form.amount;
        },

        currencySymbol() {            
            return this.supportedCurrenciesMap[this.currency]?.symbol;
        },
    },

    watch: {
        showGoalModal(newVal) {
            if (newVal) {                
                this.autoFocusGoalInput = true;
            } else {
                this.autoFocusGoalInput = false;
            }
        },

        "$route.query": {
            async handler(newVal) {                
                await this.fetchGoals({ ...newVal }, true);
            },
            deep: true,
            immediate: true,
        },
    },
    
    async mounted() {
        // await this.fetchGoals({}, true);
    },

    methods: {  
        openNewGoalModal() {
            this.showGoalModal = true;
        },

        resetCloseGoalModal() {
            this.showGoalModal = false;
            this.form = {
                name: "",
                isCompleted: false,
                target: "",
                dueDate: null,
            };
        },

        async handleScroll() {
            if ((window.innerHeight + window.scrollY + 100) >= document.body.offsetHeight) {
                await this.loadMoreGoals();
            }
        },

        async fetchGoals(params, refresh = false) {
            try {                                
                this.pageLoadingErr = "";
                this.pageLoading = true;

                const payload = {
                    page: params?.page || 1,
                    ...params,
                };
                
                await this.$store.dispatch("revenue/fetchGoals", { payload, refresh: refresh });
            } catch (error) {
                this.pageLoadingErr = true;
                
            } finally {
                this.pageLoading = false;
            }
        },

        async checkForDupliacteGoalName(name) {
            try {                
                this.pageLoadingErr = "";
                this.pageLoading = true;
                
                const res = await this.$store.dispatch("revenue/fetchGoals", { refresh: true, external: true, payload: {
                    pageNumber: 1,
                    name: name,
                }});                
                return res.length > 0;
            } catch (error) {
                this.pageLoadingErr = true;
            } finally {
                this.pageLoading = false;
            }
        },

        async handleAddNewGoal() {
            try {
                this.formGoalState = "loading";
                
                const isDuplicateName = await this.checkForDupliacteGoalName(this.form.name);

                if (isDuplicateName) {
                    this.formGoalsErrors.name = "This goal name already exists. Choose another unique name";
                    return;
                }

                const payload = {
                    ...this.form,
                    isCompleted: this.form.isCompleted === "yes" ? true : false,
                };
                
                await this.$store.dispatch("revenue/addGoal", payload);
                this.$toast.success("New goal created");
                this.resetCloseGoalModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.formGoalState = "default";
            }
        },

        setCurrentGoalForEdit(goal) {
            if (!goal._id) return;

            this.form = goal;

            this.formActionType = "edit";
            this.showGoalModal = true;
        },

        async handleMarkGoalComplete(event, goal) {
            if (!event) return;

            const value = event?.target?.checked;

            this.form.name = goal.name;
            this.form._id = goal._id;
            this.form.isCompleted = value;

            await this.updateGoal();
        },

        async updateGoal() {
            try {
                this.formGoalState = "loading";

                if (!this.form._id) return;
    
                await this.$store.dispatch("revenue/updateGoal", this.form);

                this.$toast.success("Goal updated");

                this.resetCloseGoalModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.formGoalState = "error";
                this.$toast.error(errMsg);
            } finally {
                this.formGoalState = "default";
            }
        }, 

        async handleGoalSubmit() {
            if (this.formActionType === "add") {
                this.handleAddNewGoal();
            } else {
                this.updateGoal();
            }
        },

        async deleteGoal(id) {
            try {
                this.deleteLoading = true;
    
                await this.$store.dispatch("revenue/deleteGoal", id);

                this.$toast.success("Goal deleted");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;  
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        calculateGoalProgress(dueDate) {
            let value = 0;

            if (!dueDate || typeof dueDate !== "string") return value;

            const currentDate = Date.now();
            const targetDate = new Date(dueDate).getTime();

            if (currentDate < targetDate) {
                value = (currentDate / targetDate)  * 100;
            } else {
                value = (targetDate / currentDate)  * 100;
            }
            return value;
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },
    },
};
</script>